import { DesignSchemeSet } from '@data/buildingProjects/DesignSchemeSet';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { ControllerManager } from '@gui/controller/ControllerManager';
import useNotificationStore from '@gui/store/notification';
import usePersistentStore, { useDesignSchemesBucket } from '@gui/store/persistent/persistentStore';
import { Box, Button, Checkbox, Divider, TextField, Typography } from '@mui/material';
import { DrawModule } from '@rendering/DrawingModule/DrawingModule';
import {
    BorderMode,
    BorderSource,
    DesignSchemeSetType,
    NotificationType,
    SchemeKey,
    type ContextState,
    type DeepPartial,
    type DesignSchemeState,
} from '@visualizer/types';
import React, { useEffect, useMemo, useState } from 'react';
import Grid from '../../../Geodata/grid';
import SuboptionedDropdown, { type SuboptionedDropdownItem } from '../generic/SuboptionedDropdown';
import FootprintView, { FootprintActions } from './FootprintView';

type DesignSchemeSetViewProps = {
    selectedProject: DesignSchemeSet | undefined;
    isCreating: boolean;
    onCancelCreate: () => void;
    polygonDrawerRefs: Record<string, DrawModule>;
    setContextPanel: (updates: Partial<ContextState>) => void;
    context: ContextState;
};

// =====================================
// HOOKS
// =====================================

function useDesignSchemeSets() {
    const setsState = useDesignSchemesBucket((s) => s.sets);
    const controller = ControllerManager.getInstance().getDesignSchemeController();

    const designSchemeSets = useMemo(() => controller.getDesignSchemeSets(), [setsState]);

    // Recomputes them ONLY when designSchemes.sets change in Zustand, not at every re-render:
    return designSchemeSets;
}

function useSetState(setId?: string) {
    const setState = useDesignSchemesBucket((s) => (setId ? s.sets[setId] : undefined));

    return setState;
}

const DesignSchemeSetView: React.FC<DesignSchemeSetViewProps> = ({
    selectedProject,
    isCreating,
    onCancelCreate,
    polygonDrawerRefs,
    setContextPanel,
    context,
}) => {
    // Zustand Actions (Update the state):
    const designSchemesController = ControllerManager.getInstance().getDesignSchemeController();
    const pipelineController = React.useMemo(() => ControllerManager.getInstance().getPipelineController(), []);
    const showNotification = useNotificationStore((s) => s.showNotification);

    // Zustand Variables (Reactive to state change):
    const designSchemeSets = useDesignSchemeSets();
    const selectedSetId = selectedProject?.getID();
    const selectedSetState = useSetState(selectedSetId);
    const consistent = selectedSetState?.consistent;
    const shadow = selectedSetState?.shadow;
    const keepout = selectedSetState?.keepout;
    const closeToDesignSchemes = selectedSetState?.closeToDesignSchemes;
    const selectedFootprintId = selectedSetState?.selectedFootprintId ?? null;

    // React State:
    const footprintRefs = React.useRef<Record<string, FootprintActions | null>>({});

    // React stuff to migrate to Zustand later:
    const defaultSetName = `Design Scheme ${designSchemeSets.length + 1}`;
    const [newName, setNewName] = useState(defaultSetName);
    const [footprintType, setFootprintType] = useState<DesignSchemeSetType | null>(DesignSchemeSetType.FULL_SITE);
    const [pendingSchemeType, setPendingSchemeType] = useState<SchemeKey>(SchemeKey.FLOOR_BASED);
    const [isEditingFootprint, setIsEditingFootprint] = useState(false);

    const schemeType = useMemo(() => {
        if (!selectedSetState?.footprints) return SchemeKey.FLOOR_BASED;

        // Return the type of the designScheme of the current active footprint:
        if (selectedFootprintId) {
            const dsMap = selectedSetState.footprints[selectedFootprintId]?.designSchemes;
            const active = dsMap ? Object.values(dsMap).find((ds) => ds?.visibility === true) : undefined;
            if (active?.schemeKey) return active.schemeKey;
        }

        // During initial creation, a footprint may exist before selectedFootprintId is set.
        // In that window, read any active scheme from the set instead of defaulting to FLOOR_BASED.
        for (const footprint of Object.values(selectedSetState.footprints)) {
            const active = footprint?.designSchemes
                ? Object.values(footprint.designSchemes).find((ds) => ds?.visibility === true)
                : undefined;
            if (active?.schemeKey) return active.schemeKey;
        }

        return SchemeKey.FLOOR_BASED;
    }, [selectedSetState, selectedFootprintId]);

    useEffect(() => {
        setIsEditingFootprint(false);
    }, [selectedFootprintId]);

    useEffect(() => {
        // Synchs the local footprintType with the selected project updates (will be removed when using Zustand)
        if (!isCreating && selectedProject) {
            setFootprintType(selectedProject.getType());
            setNewName(selectedSetState?.name ?? '');
        }
    }, [selectedProject, selectedSetState?.name, isCreating]);

    useEffect(() => {
        // Resets name when switching to creating mode
        if (isCreating) {
            setNewName(defaultSetName);
            setPendingSchemeType(schemeType);
        }
    }, [isCreating, defaultSetName, schemeType]);

    // Handlers:
    const handleAdd = (type: DesignSchemeSetType) => {
        const designSchemeSet = designSchemesController.addDesignSchemeSet(newName, type);
        if (!designSchemeSet) return;

        const setId = designSchemeSet.getID();

        // FULL_SITE:
        if (type === DesignSchemeSetType.FULL_SITE) {
            const footprint = Grid.getPoints();
            designSchemesController.addFootprint(setId, footprint);

            const fps = designSchemesController.getFootprints(setId);
            const fp = fps?.[fps.length - 1];
            if (fp) {
                // Create DesignScheme immediately:
                designSchemesController.ensureActiveScheme(setId, fp.id, pendingSchemeType);
            }
        }

        // HAND_DRAWN:
        if (type === DesignSchemeSetType.HAND_DRAWN) {
            designSchemesController.addFootprint(setId, []);

            const fps = designSchemesController.getFootprints(setId);
            const firstFootprint = fps?.[fps.length - 1];
            if (firstFootprint) {
                // Create DesignScheme immediately:
                designSchemesController.ensureActiveScheme(setId, firstFootprint.id, pendingSchemeType);

                // Select footprint:
                designSchemesController.updateDesignSchemeSet(setId, {
                    selectedFootprintId: firstFootprint.id,
                });

                // Enter edit mode:
                setTimeout(() => {
                    footprintRefs.current[firstFootprint.id]?.toggleEdit();
                    setIsEditingFootprint(true);
                }, 0);
            }

            // Deactivate OuterSPs by default:

            designSchemesController.updateDesignSchemeSet(setId, { outterSP: false });
        }

        // Finally select the new set
        designSchemesController.updateSelectedDesignSchemeSet(setId);
    };

    // Used to enable live renaming of DesignSchemeSets:
    const commitRename = () => {
        if (isCreating || !selectedSetId || !selectedSetState) return;

        const trimmed = newName.trim();
        const currentName = selectedSetState.name;
        const nextName = trimmed.length > 0 ? trimmed : currentName;

        if (nextName !== currentName) {
            designSchemesController.updateDesignSchemeSet(selectedSetId, { name: nextName });
        }

        if (nextName !== newName) {
            setNewName(nextName);
        }
    };

    const handleSchemeTypeChange = (newType: SchemeKey) => {
        setPendingSchemeType(newType);

        if (isCreating) return;
        if (!selectedProject || !selectedSetId || !selectedFootprintId) return;

        if (!consistent) {
            // Apply ONLY to selected footprint:
            designSchemesController.ensureActiveScheme(selectedSetId, selectedFootprintId, newType);
            designSchemesController.refreshPostBlocksWhenSetGeometryReady(selectedSetId, true);
            return;
        }

        // Apply to ALL footprints:
        const fps = designSchemesController.getFootprints(selectedSetId) ?? [];
        fps.forEach((fp) => {
            designSchemesController.ensureActiveScheme(selectedSetId, fp.id, newType);
        });
        designSchemesController.refreshPostBlocksWhenSetGeometryReady(selectedSetId, true);
    };

    const schemeTypeDropdownItems: SuboptionedDropdownItem<SchemeKey>[] = useMemo(
        () => [
            {
                type: 'group',
                id: 'single-volume',
                label: 'Single Volume',
                testId: 'ds-type-singlevolume-toggle',
            },
            {
                type: 'option',
                value: SchemeKey.CONVEX_HULL,
                label: 'Hull',
                parentGroupId: 'single-volume',
                testId: 'ds-type-convexhull',
            },
            {
                type: 'option',
                value: SchemeKey.ORIENTED_VOLUME,
                label: 'Oriented Volume',
                parentGroupId: 'single-volume',
                testId: 'ds-type-orientedvolume',
            },
            {
                type: 'option',
                value: SchemeKey.BOX,
                label: 'Box',
                parentGroupId: 'single-volume',
                testId: 'ds-type-box',
            },
            {
                type: 'option',
                value: SchemeKey.FLOOR_BASED,
                label: 'Floor Based',
                testId: 'ds-type-floorbased',
            },
        ],
        []
    );

    const hasSets = designSchemeSets.length > 0;
    const footprintDropdownTooltip = 'Polygon(s) on which the Design Scheme is applied.';
    const typeDropdownTooltip = 'Type of Design Scheme to be applied.';
    const footprintDropdownItems: SuboptionedDropdownItem<DesignSchemeSetType>[] = useMemo(
        () => [
            {
                type: 'option',
                value: DesignSchemeSetType.FULL_SITE,
                label: 'Full Site (1)',
            },
            {
                type: 'option',
                value: DesignSchemeSetType.HAND_DRAWN,
                label: 'Hand Drawn (n)',
                testId: 'footprint-hand-drawn',
            },
            {
                type: 'option',
                value: DesignSchemeSetType.DATA_BASED,
                label: 'Data Based (n)',
                disabled: true,
            },
        ],
        []
    );
    const selectedFootprintType =
        (selectedProject?.getType() && !isCreating ? selectedProject.getType() : footprintType) ?? DesignSchemeSetType.FULL_SITE;

    // Footprint selection:
    const footprints = designSchemesController.getFootprints(selectedProject?.getID() ?? '') ?? [];

    // Auto-select first footprint when in FULL_SITE mode:
    useEffect(() => {
        if (selectedProject?.getType() === DesignSchemeSetType.FULL_SITE && footprints.length > 0 && selectedFootprintId === null) {
            designSchemesController.updateDesignSchemeSet(selectedSetId ?? '', { selectedFootprintId: footprints[0].id });
        }
    }, [selectedProject?.getID(), footprints]);

    // Footprint Carousel helpers:
    const footprintIndex = useMemo(() => {
        if (!selectedFootprintId) return -1;
        return footprints.findIndex((f) => f.id === selectedFootprintId);
    }, [selectedFootprintId, footprints]);

    const hasPrev = footprintIndex > 0;
    const hasNext = footprintIndex < footprints.length - 1;
    const selectedFootprint = selectedFootprintId ? footprints.find((fp) => fp.id === selectedFootprintId) : undefined;

    const selectFootprintByIndex = (idx: number) => {
        const fp = footprints[idx];
        if (!fp || !selectedSetId) return;

        designSchemesController.updateDesignSchemeSet(selectedSetId, {
            selectedFootprintId: fp.id,
        });
    };

    // Checks if the currently selected Footprint's Design Scheme uses Hand Drawn borders:
    const selectedActiveSchemeUsesHandDrawn = useMemo(() => {
        if (!selectedSetState || !selectedFootprintId) return false;

        // 1) Get all footprints design schemes:
        const dsMap = selectedSetState.footprints?.[selectedFootprintId]?.designSchemes;
        if (!dsMap) return false;

        // 2) Get active design scheme:
        const activeScheme =
            Object.values(dsMap).find((ds) => ds?.visibility) ?? Object.values(dsMap).find((ds) => ds?.schemeKey === schemeType);
        if (!activeScheme || activeScheme.schemeKey !== SchemeKey.FLOOR_BASED || !activeScheme.floorBased?.borderState) return false;

        // 3) Check if it uses Hand Drawn borders:
        const borderState = activeScheme.floorBased.borderState;
        if (borderState.mode === BorderMode.SINGLE) return borderState.single === BorderSource.HAND_DRAWN;
        return borderState.external === BorderSource.HAND_DRAWN || borderState.internal === BorderSource.HAND_DRAWN;
    }, [selectedSetState, selectedFootprintId, schemeType]);

    // Consistent synchronize all footprints when toggled on:
    useEffect(() => {
        if (!consistent) return;
        if (!selectedSetId) return;

        // 1. Ensure same scheme type:
        const fps = designSchemesController.getFootprints(selectedSetId) ?? [];
        fps.forEach((fp) => {
            designSchemesController.ensureActiveScheme(selectedSetId, fp.id, schemeType);
        });

        // 2. Get current footprint (fallback to first):
        const sourceFpId = selectedFootprintId ?? fps[0]?.id;
        if (!sourceFpId) return;

        const latestSetState = usePersistentStore.getState().designSchemes.sets[selectedSetId];
        const sourceDsMap = latestSetState?.footprints?.[sourceFpId]?.designSchemes;
        if (!sourceDsMap) return;

        // 3. Get current active Design Scheme:
        const sourceEntry =
            Object.entries(sourceDsMap).find(([, ds]) => ds?.visibility) ??
            Object.entries(sourceDsMap).find(([, ds]) => ds?.schemeKey === schemeType);
        if (!sourceEntry) return;

        const [sourceSchemeId, sourceScheme] = sourceEntry;

        // 4. Get all settings of current Scheme:
        const syncPartial: DeepPartial<DesignSchemeState> = {
            linkedLayerIds: sourceScheme.linkedLayerIds,
        };

        if (sourceScheme.schemeKey === SchemeKey.FLOOR_BASED && sourceScheme.floorBased) {
            const cfg = JSON.parse(JSON.stringify(sourceScheme.floorBased));
            delete cfg.active;
            syncPartial.floorBased = cfg;
        }

        if (sourceScheme.schemeKey === SchemeKey.CONVEX_HULL && sourceScheme.convexHull) {
            const cfg = JSON.parse(JSON.stringify(sourceScheme.convexHull));
            delete cfg.active;
            syncPartial.convexHull = cfg;
        }

        if (sourceScheme.schemeKey === SchemeKey.ORIENTED_VOLUME && sourceScheme.orientedVolume) {
            const cfg = JSON.parse(JSON.stringify(sourceScheme.orientedVolume));
            delete cfg.active;
            syncPartial.orientedVolume = cfg;
        }

        if (sourceScheme.schemeKey === SchemeKey.BOX && sourceScheme.box) {
            const cfg = JSON.parse(JSON.stringify(sourceScheme.box));
            delete cfg.active;
            syncPartial.box = cfg;
        }

        // 5. Update DesignScheme of ALL footprints (Method knows if we are in consistent mode or not):
        designSchemesController.updateDesignScheme(selectedSetId, sourceFpId, sourceSchemeId, syncPartial);
    }, [consistent, selectedSetId, selectedFootprintId, schemeType]);

    return (
        <Box>
            {(hasSets || isCreating) && (
                <>
                    <Divider sx={{ my: 0.5 }} />

                    {/* === CREATE VIEW === */}
                    <Box display="flex" gap={1} mt={1} alignItems="center">
                        {/* --- Name --- */}
                        <TextField
                            id="outlined-basic"
                            label="Name"
                            variant="outlined"
                            size="small"
                            sx={{
                                flex: 1,
                                minWidth: 0,

                                // Match SELECT styling exactly:
                                '& .MuiInputBase-root': {
                                    padding: 0,
                                    height: 28,
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: '5px 7px',
                                    fontSize: '8px',
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '10px',
                                },
                            }}
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onBlur={commitRename}
                            onKeyDown={(e) => {
                                if (e.key !== 'Enter') return;
                                commitRename();
                                (e.target as HTMLInputElement).blur();
                            }}
                        />

                        {/* --- Footprint --- */}
                        <SuboptionedDropdown
                            value={selectedFootprintType}
                            onChange={setFootprintType}
                            fieldLabel="Footprint"
                            fieldLabelTooltip={footprintDropdownTooltip}
                            selectTestId="footprint-select"
                            disabled={!isCreating}
                            items={footprintDropdownItems}
                        />

                        {/* --- Type --- */}
                        <SuboptionedDropdown
                            value={isCreating ? pendingSchemeType : schemeType}
                            onChange={handleSchemeTypeChange}
                            fieldLabel="Type"
                            fieldLabelTooltip={typeDropdownTooltip}
                            selectTestId="ds-type-select"
                            items={schemeTypeDropdownItems}
                        />

                        {/* --- Consistent Checkbox --- */}
                        {!isCreating &&
                            selectedSetState &&
                            selectedSetId &&
                            selectedProject?.getType() !== DesignSchemeSetType.FULL_SITE &&
                            footprints.length > 1 && (
                                <Box
                                    sx={{
                                        flex: 0.25,
                                        minWidth: 0,
                                        height: 28,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'flex-start',
                                            height: '100%',
                                        }}
                                    >
                                        <CustomTooltip side="right" mode="hover" title="Consistent Type across Footprints">
                                            <Typography
                                                sx={{
                                                    fontSize: '8px',
                                                    lineHeight: 1,
                                                    whiteSpace: 'nowrap',
                                                    margin: 0,
                                                }}
                                            >
                                                Cons
                                            </Typography>
                                        </CustomTooltip>

                                        <Checkbox
                                            size="small"
                                            checked={consistent}
                                            onChange={(e) => {
                                                const nextConsistent = e.target.checked;

                                                // Prevent enabling Consistent if selected Footprint's DesignScheme uses Hand Drawn borders:
                                                if (nextConsistent && selectedActiveSchemeUsesHandDrawn) {
                                                    showNotification(
                                                        'Consistent cannot be enabled while the selected footprint uses Hand Drawn borders.',
                                                        NotificationType.Warning
                                                    );
                                                    return;
                                                }

                                                designSchemesController.updateDesignSchemeSet(selectedSetId, {
                                                    consistent: nextConsistent,
                                                });
                                            }}
                                            sx={{
                                                padding: 0,
                                                margin: 0,
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '14px',
                                                },
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                    </Box>

                    {/* --- Add --- */}
                    {isCreating && (
                        <Box sx={{ my: 0.5 }} textAlign="center">
                            <Button
                                data-testid="add-ds"
                                sx={{ width: '25%' }}
                                variant="contained"
                                size="small"
                                onClick={() => {
                                    handleAdd(footprintType!);
                                    onCancelCreate();
                                }}
                            >
                                Add
                            </Button>
                        </Box>
                    )}

                    {/* === FLAGS (InnerSP, Shadow, Keepout) when NOT creating === */}

                    {!isCreating && selectedSetState && selectedSetId && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, my: 0.5 }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                    <Checkbox
                                        size="small"
                                        checked={selectedSetState.innerSP}
                                        onChange={() => {
                                            const newState = !selectedSetState.innerSP;
                                            designSchemesController.updateDesignSchemeSet(selectedSetId, { innerSP: newState });
                                            pipelineController.run();
                                        }}
                                        sx={{
                                            padding: 0,
                                            '& .MuiSvgIcon-root': { fontSize: '14px' },
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>InnerSP</Typography>
                                </Box>

                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                    <Checkbox
                                        data-testid="outer-sp-checkbox"
                                        size="small"
                                        checked={selectedSetState.outterSP}
                                        onChange={() => {
                                            const newState = !selectedSetState.outterSP;
                                            designSchemesController.updateDesignSchemeSet(selectedSetId, { outterSP: newState });
                                            pipelineController.run();
                                        }}
                                        sx={{
                                            padding: 0,
                                            '& .MuiSvgIcon-root': { fontSize: '14px' },
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>OuterSP</Typography>
                                </Box>

                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                    <Checkbox
                                        size="small"
                                        checked={!!closeToDesignSchemes}
                                        onChange={() => {
                                            designSchemesController.updateDesignSchemeSet(selectedSetId, {
                                                closeToDesignSchemes: !closeToDesignSchemes,
                                            });
                                        }}
                                        sx={{
                                            padding: 0,
                                            '& .MuiSvgIcon-root': { fontSize: '14px' },
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>CloseSP</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {import.meta.env.MODE === 'development' && (
                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                        <Checkbox
                                            size="small"
                                            checked={shadow}
                                            onChange={() => {
                                                designSchemesController.updateDesignSchemeSet(selectedSetId, {
                                                    shadow: !shadow,
                                                });
                                                pipelineController.run();
                                            }}
                                            sx={{
                                                padding: 0,
                                                '& .MuiSvgIcon-root': { fontSize: '14px' },
                                            }}
                                        />
                                        <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Shadow</Typography>
                                    </Box>
                                )}

                                {import.meta.env.MODE === 'development' && (
                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                        <Checkbox
                                            size="small"
                                            checked={keepout}
                                            onChange={() => {
                                                designSchemesController.updateDesignSchemeSet(selectedSetId, {
                                                    keepout: !keepout,
                                                });
                                                pipelineController.run();
                                            }}
                                            sx={{
                                                padding: 0,
                                                '& .MuiSvgIcon-root': { fontSize: '14px' },
                                            }}
                                        />
                                        <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Keepout</Typography>
                                    </Box>
                                )}

                                <Box sx={{ flex: 1 }} />
                            </Box>
                        </Box>
                    )}

                    {footprintType !== DesignSchemeSetType.FULL_SITE && <Divider sx={{ my: 0.5 }} />}
                </>
            )}

            {!isCreating && selectedSetId && selectedSetState && selectedProject && (
                <>
                    {selectedSetId && selectedProject?.getType() === DesignSchemeSetType.HAND_DRAWN && (
                        <Box display="flex" alignItems="center" gap={1} sx={{ my: 1 }}>
                            {/* ─── Carousel ───────────────────── */}
                            <Box
                                sx={{
                                    flex: 0.3,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    px: 0.5,
                                    height: 28,
                                }}
                            >
                                <Button
                                    size="small"
                                    disabled={!hasPrev || footprints.length === 0 || isEditingFootprint || consistent}
                                    onClick={() => selectFootprintByIndex(footprintIndex - 1)}
                                >
                                    ‹
                                </Button>

                                <Typography
                                    sx={{
                                        fontSize: '10px',
                                        whiteSpace: 'nowrap',
                                        textAlign: 'center',
                                        flex: 1,
                                        opacity: footprints.length === 0 ? 0.6 : 1,
                                    }}
                                >
                                    {footprints.length === 0
                                        ? 'Add Footprint'
                                        : consistent && footprints.length > 1
                                          ? 'All Footprints'
                                          : `Footprint ${footprintIndex + 1}`}
                                </Typography>

                                <Button
                                    size="small"
                                    disabled={!hasNext || footprints.length === 0 || isEditingFootprint || consistent}
                                    onClick={() => selectFootprintByIndex(footprintIndex + 1)}
                                >
                                    ›
                                </Button>
                            </Box>

                            {/* ─── Add ────────────────────────── */}
                            <Button
                                size="small"
                                variant="contained"
                                disabled={isEditingFootprint}
                                sx={{ flex: 0.23, minWidth: 0 }}
                                onClick={() => {
                                    if (!selectedProject || !selectedSetId) return;

                                    const setId = selectedProject.getID();

                                    // 1) Create footprint
                                    designSchemesController.addFootprint(setId, []);

                                    const fps = designSchemesController.getFootprints(setId);
                                    const newFootprint = fps?.[fps.length - 1];
                                    if (!newFootprint) return;

                                    // 2) Activate DesignScheme immediately (THIS was missing conceptually)
                                    designSchemesController.ensureActiveScheme(setId, newFootprint.id, schemeType);

                                    // 3) Select footprint
                                    designSchemesController.updateDesignSchemeSet(selectedSetId, {
                                        selectedFootprintId: newFootprint.id,
                                    });

                                    // 4) Enter edit mode
                                    setTimeout(() => {
                                        footprintRefs.current[newFootprint.id]?.toggleEdit();

                                        // ✅ keep the UI label in sync
                                        setIsEditingFootprint(true);
                                    }, 0);
                                }}
                            >
                                Add
                            </Button>

                            {/* ─── Edit / Close ───────────────── */}
                            <Button
                                size="small"
                                variant="contained"
                                disabled={footprints.length === 0}
                                sx={{ flex: 0.23, minWidth: 0 }}
                                onClick={() => {
                                    if (!selectedFootprintId) return;

                                    footprintRefs.current[selectedFootprintId]?.toggleEdit();
                                }}
                            >
                                {isEditingFootprint ? 'Close' : 'Edit'}
                            </Button>

                            {/* ─── Delete ─────────────────────── */}
                            <Button
                                size="small"
                                color="error"
                                variant="contained"
                                disabled={footprints.length === 0}
                                sx={{ flex: 0.23, minWidth: 0 }}
                                onClick={() => {
                                    if (!selectedFootprintId) return;
                                    const fpId = selectedFootprintId;
                                    const api = footprintRefs.current[fpId];
                                    if (!api) return;

                                    // Exit edit mode first:
                                    if (api.isEditing()) {
                                        api.toggleEdit();
                                    }
                                    setIsEditingFootprint(false);

                                    // Delete footprint:
                                    setTimeout(() => {
                                        api.delete();
                                        delete footprintRefs.current[fpId];
                                    }, 0);
                                }}
                            >
                                Delete
                            </Button>
                        </Box>
                    )}

                    {/* ─── Per-Footprint Sections ───────────────────────────── */}
                    {selectedFootprint && (
                        <FootprintView
                            key={selectedFootprint.id}
                            ref={(api) => {
                                footprintRefs.current[selectedFootprint.id] = api;
                            }}
                            footprint={selectedFootprint}
                            idx={footprints.findIndex((fp) => fp.id === selectedFootprint.id)}
                            selectedProject={selectedProject}
                            polygonDrawerRefs={polygonDrawerRefs}
                            context={context}
                            setContextPanel={setContextPanel}
                            onEditEnd={() => {
                                setIsEditingFootprint(false);
                            }}
                            onEditStart={() => {
                                setIsEditingFootprint(true);
                            }}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default DesignSchemeSetView;
